import { enumKeyOf } from "_shared/_utilities/enum-utils";
import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import AHBoolean from "_shared/booleans/boolean";
import AHDate from "_shared/dates/date";
import EntityMember from "_shared/entity-members/entity-member";
import EntityMemberSchema from "_shared/entity-members/entity-member-schema";
import FeeScheduleCategorySchema from "_shared/fee-schedules/categories/fee-schedule-category-schema";
import Guid from "_shared/guids/guid";
import User from "users/user";
import { MaskedContracts, MaskedDiscount, MaskedFeeSchedule, MaskedTeam } from "work/_shared/masked-fields";
import { ProposalAction, ProposalStatus } from "work/proposals/__constants/constants";
import ProposalBuilderSchema, {
  ProposalBuilderJSON,
  ProposalBuilderParams,
} from "work/proposals/_schemas/proposal-builder-schema";
import ProposalRedline, { ProposalRedlineJSON } from "work/proposals/redlining/proposal-redline";
import ProposalReviewerSchema from "work/proposals/reviewers/_schemas/proposal-reviewer-schema";
import ProposalReviewer from "work/proposals/reviewers/proposal-reviewer";
import WorkAgreementSchema from "work/work-agreements/_schemas/work-agreement-codec";
import { WorkAgreementZ } from "work/work-agreements/_schemas/work-agreement-schema";
import WorkDocumentSchema from "work/work-agreements/contracts/_schemas/work-document-schema";
import ClientTeamSchema from "work/work-agreements/teams/client-team/_schema/client-team-schema";
import VendorTeamSchema from "work/work-agreements/teams/vendor-team/_schemas/vendor-team-schema";
import { z } from "zod";

type BaseProposalJSON = z.infer<typeof ProposalSchema.BaseProposalJSONZ>;
type BaseProposalParams = z.infer<typeof ProposalSchema.BaseProposalZ>;

export type ProposalJSON = BaseProposalJSON & {
  userBuilderJSON?: ProposalBuilderJSON | string | null;
  partyRevision?: ProposalJSON | null;
  userRevision?: ProposalJSON | null;
  supersedes?: ProposalJSON | null;
  existingRedlineJSON?: ProposalRedlineJSON | null;
};

export type ProposalParams = BaseProposalParams & {
  userBuilder?: ProposalBuilderParams;
  partyRevision?: ProposalParams;
  userRevision?: ProposalParams;
  supersedes?: ProposalParams;
  existingRedline?: ProposalRedline;
};

export default class ProposalSchema {
  private readonly workAgreementSchema: WorkAgreementSchema;
  private readonly currentUser: User;
  
  public constructor(workAgreementSchema: WorkAgreementSchema, currentUser: User) {
    this.workAgreementSchema = workAgreementSchema;
    this.currentUser = currentUser;
  }

  public static readonly BaseProposalJSONZ = z.object({
    rfpId: z.uuid().nullish().or(z.literal(Guid.EMPTY.value)),
    id: z.uuid().nullish(),
    name: z.string().nullish(),
    description: z.string().nullish(),
    creator: EntityMemberSchema.EntityMemberJSONZ,
    createdDate: z.iso.datetime({ offset: true }).nullish(),
    lastUpdated: z.iso.datetime({ offset: true }).nullish(),
    workAgreementId: z.uuid().nullish(),
    negotiable: z.boolean(),
    responseDueBy: z.iso.datetime({ offset: true }).nullish(),
    clientReviewers: z.array(ProposalReviewerSchema.ProposalReviewerJSONZ),
    vendorReviewers: z.array(ProposalReviewerSchema.ProposalReviewerJSONZ),
    supersededById: z.uuid().nullish(),
    status: z.string().nullish(),
    availableActions: z.partialRecord(z.enum(ProposalAction), z.array(z.string())).nullish(),
    clientTeam: z
      .intersection(ClientTeamSchema.ClientTeamJSONZ.optional(), z.object({ isMasked: z.boolean() }))
      .nullish(),
    additionalVendors: z.array(EntityMemberSchema.EntityMemberJSONZ).nullish(),
    vendorTeam: z
      .intersection(VendorTeamSchema.VendorTeamJSONZ.optional(), z.object({ isMasked: z.boolean() }))
      .nullish(),
    teamRestricted: z
      .object({
        isMasked: z.boolean(),
        value: z.boolean().nullish(),
      })
      .nullish(),
    feeSchedule: z.object({
      isMasked: z.boolean(),
      categories: z.array(FeeScheduleCategorySchema.FeeScheduleCategoryJSONZ).nullish(),
    }),
    startDate: z.iso.datetime({ offset: true }).nullish(),
    endDate: z.iso.datetime({ offset: true }).nullish(),
    discount: z.object({ percent: z.number().nullable(), isMasked: z.boolean() }).nullish(),
    clientPolicies: z
      .object({
        isMasked: z.boolean(),
        documents: z.array(
          z.object({
            documentId: z.uuid().nullish(),
            documentInfo: WorkDocumentSchema.WorkDocumentJSONZ,
            replacesId: z.uuid().nullish(),
          }),
        ).optional(),
      })
      .nullish(),
    vendorPolicies: z
      .object({
        isMasked: z.boolean(),
        documents: z.array(
          z.object({
            documentId: z.uuid().nullish(),
            documentInfo: WorkDocumentSchema.WorkDocumentJSONZ,
            replacesId: z.uuid().nullish(),
          }),
        ).optional()
      })
      .nullish(),
    conflictsCheckWaived: z
      .object({
        isMasked: z.boolean(),
        value: z.boolean().nullish(),
      })
      .nullish(),
    conflicts: z
      .object({
        isMasked: z.boolean(),
        documents: z.array(
          z.object({
            documentId: z.uuid().nullish(),
            documentInfo: WorkDocumentSchema.WorkDocumentJSONZ,
            replacesId: z.uuid().nullish(),
          }),
        ).optional()
      })
      .nullish(),
    clientTeamTemplateIds: z.array(z.uuid()).nullish(),
    vendorTeamTemplateIds: z.array(z.uuid()).nullish(),
    clientFeeScheduleTemplateIds: z.array(z.uuid()).nullish(),
    vendorFeeScheduleTemplateIds: z.array(z.uuid()).nullish(),
    hireDate: z.iso.datetime({ offset: true }).nullish(),
    archiveDate: z.iso.datetime({ offset: true }).nullish(),
  });

  public static readonly ProposalJSONZ: z.ZodType<ProposalJSON> = ProposalSchema.BaseProposalJSONZ.extend({
    userBuilderJSON: z.lazy(() => z.string().nullish().or(ProposalBuilderSchema.ProposalBuilderJSONZ.nullish())),
    partyRevision: z.lazy(() => ProposalSchema.ProposalJSONZ.nullish()),
    userRevision: z.lazy(() => ProposalSchema.ProposalJSONZ.nullish()),
    supersedes: z.lazy(() => ProposalSchema.ProposalJSONZ.nullish()),
    existingRedlineJSON: z.lazy(() => z.custom<ProposalRedlineJSON>().nullish()),
  });

  public static readonly ProposalJSONWithoutBuilderZ: z.ZodType<ProposalJSON> = ProposalSchema.BaseProposalJSONZ.extend(
    {
      partyRevision: z.lazy(() => ProposalSchema.ProposalJSONWithoutBuilderZ.nullish()),
      userRevision: z.lazy(() => ProposalSchema.ProposalJSONWithoutBuilderZ.nullish()),
      supersedes: z.lazy(() => ProposalSchema.ProposalJSONWithoutBuilderZ.nullish()),
      existingRedlineJSON: z.lazy(() => z.custom<ProposalRedlineJSON>().nullish()),
    },
  );

  public static readonly BaseProposalZ = z.object({
    id: z.instanceof(Guid).optional(),
    creator: z.instanceof(EntityMember),
    createdDate: z.instanceof(AHDate).optional(),
    lastUpdated: z.instanceof(AHDate).optional(),
    workAgreement: z.lazy(() => WorkAgreementZ),
    responseDueBy: z.instanceof(AHDate).optional(),
    clientReviewers: z.array(z.instanceof(ProposalReviewer)).default([]),
    vendorReviewers: z.array(z.instanceof(ProposalReviewer)).default([]),
    supersededById: z.instanceof(Guid).optional(),
    status: z.enum(ProposalStatus).optional(),
    availableActions: z.record(z.enum(ProposalAction), z.array(z.instanceof(Guid))).optional(),
    negotiable: z.instanceof(AHBoolean).optional().default(new AHBoolean(true)),
  });

  public static readonly ProposalZ: z.ZodType<ProposalParams> = ProposalSchema.BaseProposalZ.extend({
    userBuilder: z.lazy(() => ProposalBuilderSchema.ProposalBuilderZ.optional()),
    partyRevision: z.lazy(() => ProposalSchema.ProposalZ.optional()),
    userRevision: z.lazy(() => ProposalSchema.ProposalZ.optional()),
    supersedes: z.lazy(() => ProposalSchema.ProposalZ.optional()),
    existingRedline: z.lazy(() => z.instanceof(ProposalRedline).optional()),
  }).readonly();

  public static validate(values: unknown): ProposalParams {
    return parseOrThrow(ProposalSchema.ProposalZ, values, {
      logError: true,
      schemaName: "ProposalSchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(json: unknown): ProposalJSON {
    return parseOrThrow(ProposalSchema.ProposalJSONZ, json, {
      logError: true,
      schemaName: "ProposalSchema",
      operation: "validateJSON",
      payloadSample: json,
    });
  }

  public static validateJSONArray(jsonArray: unknown): ProposalJSON[] {
    return parseOrThrow(z.array(ProposalSchema.ProposalJSONZ), jsonArray, {
      logError: true,
      schemaName: "ProposalSchema",
      operation: "validateJSONArray",
      payloadSample: jsonArray,
    });
  }

  public static isValid(values: unknown): values is ProposalParams {
    try {
      ProposalSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(json: unknown): json is ProposalJSON {
    try {
      ProposalSchema.validateJSON(json);
      return true;
    } catch {
      return false;
    }
  }

  public deserialize(json: unknown): ProposalParams {
    const parsed = parseOrThrow(ProposalSchema.ProposalJSONZ, json, {
      logError: true,
      schemaName: "ProposalSchema",
      operation: "deserialize",
      payloadSample: json,
    });
    return this.codec.decode(parsed);
  }

  public serialize(params: ProposalParams): ProposalJSON {
    const parsed = ProposalSchema.validate(params);
    return this.codec.encode(parsed) as ProposalJSON;
  }

  private readonly codec = z.codec(
    ProposalSchema.ProposalJSONZ,
    ProposalSchema.ProposalZ,
    {
      decode: (value, _payload): ProposalParams => {
        const data = ProposalSchema.validateJSON(value);

        return {
          id: Guid.fromJSON(data.id) ?? undefined,
          creator: new EntityMember(EntityMemberSchema.deserialize(data.creator)),
          createdDate: AHDate.fromJSON(data.createdDate) ?? undefined,
          lastUpdated: AHDate.fromJSON(data.lastUpdated) ?? undefined,
          workAgreement: this.workAgreementSchema.deserialize(data),
          negotiable: AHBoolean.fromJSON(data.negotiable) ?? new AHBoolean(false),
          responseDueBy: AHDate.fromJSON(data.responseDueBy) ?? undefined,
          clientReviewers:
            data.clientReviewers.map((r) => new ProposalReviewer(ProposalReviewerSchema.deserialize(r))) ?? [],
          vendorReviewers:
            data.vendorReviewers.map((r) => new ProposalReviewer(ProposalReviewerSchema.deserialize(r))) ?? [],
          supersededById: Guid.fromJSON(data.supersededById) ?? undefined,
          status: ProposalStatus[data.status as keyof typeof ProposalStatus],
          availableActions: {
            [ProposalAction.Submit]:
              data.availableActions?.[ProposalAction.Submit]
                ?.map((id) => Guid.fromJSON(id))
                .filter((val) => !isNullOrUndefined(val)) ?? [],
            [ProposalAction.Approve]:
              data.availableActions?.[ProposalAction.Approve]
                ?.map((id) => Guid.fromJSON(id))
                .filter((val) => !isNullOrUndefined(val)) ?? [],
            [ProposalAction.Reject]:
              data.availableActions?.[ProposalAction.Reject]
                ?.map((id) => Guid.fromJSON(id))
                .filter((val) => !isNullOrUndefined(val)) ?? [],
            [ProposalAction.Revise]:
              data.availableActions?.[ProposalAction.Revise]
                ?.map((id) => Guid.fromJSON(id))
                .filter((val) => !isNullOrUndefined(val)) ?? [],
            [ProposalAction.Hire]:
              data.availableActions?.[ProposalAction.Hire]
                ?.map((id) => Guid.fromJSON(id))
                .filter((val) => !isNullOrUndefined(val)) ?? [],
            [ProposalAction.Delete]:
              data.availableActions?.[ProposalAction.Delete]
                ?.map((id) => Guid.fromJSON(id))
                .filter((val) => !isNullOrUndefined(val)) ?? [],
            [ProposalAction.Cancel]:
              data.availableActions?.[ProposalAction.Cancel]
                ?.map((id) => Guid.fromJSON(id))
                .filter((val) => !isNullOrUndefined(val)) ?? [],
            [ProposalAction.Edit]:
              data.availableActions?.[ProposalAction.Edit]
                ?.map((id) => Guid.fromJSON(id))
                .filter((val) => !isNullOrUndefined(val)) ?? [],
            [ProposalAction.Review]:
              data.availableActions?.[ProposalAction.Review]
                ?.map((id) => Guid.fromJSON(id))
                .filter((val) => !isNullOrUndefined(val)) ?? [],
          },
          partyRevision: data.partyRevision ? this.deserialize(data.partyRevision) : undefined, // @brad I had to change this because the format was { json: ..., currentUser: ... } but it's looking for proposal JSON
          userRevision: data.userRevision ? this.deserialize(data.userRevision) : undefined,
          supersedes: data.supersedes ? this.deserialize(data.supersedes) : undefined,
          userBuilder: (() => {
            if (typeof data.userBuilderJSON === "string") {
              return new ProposalBuilderSchema(this.workAgreementSchema, this.currentUser).deserialize(
                JSON.parse(data.userBuilderJSON),
              );
            }
            if (data.userBuilderJSON) {
              return new ProposalBuilderSchema(this.workAgreementSchema, this.currentUser).deserialize(
                data.userBuilderJSON,
              );
            }
          })(),
          existingRedline: ProposalRedline.fromJSON(data.existingRedlineJSON) ?? undefined,
        }
      },
      encode: (value, _payload): ProposalJSON => {
        const proposalParams = ProposalSchema.validate(value);

        if (!proposalParams.creator) {
          throw new Error("Cannot serialize proposal without a creator.");
        }
        if (!proposalParams.workAgreement) {
          throw new Error("Cannot serialize proposal without a work agreement.");
        }

        let clientTeam;
        let vendorTeam;
        let feeSchedule;
        let clientPolicies;
        let vendorPolicies;
        let conflicts;

        if (MaskedTeam.isNotMasked(proposalParams.workAgreement.clientTeam)) {
          clientTeam = proposalParams.workAgreement.clientTeam
            ? {
              isMasked: false,
              ...ClientTeamSchema.serialize(proposalParams.workAgreement.clientTeam),
            }
            : undefined;
        } else {
          clientTeam = { isMasked: true };
        }

        if (MaskedTeam.isNotMasked(proposalParams.workAgreement.vendorTeam)) {
          vendorTeam = proposalParams.workAgreement.vendorTeam
            ? {
              isMasked: false,
              ...VendorTeamSchema.serialize(proposalParams.workAgreement.vendorTeam),
            }
            : undefined;
        } else {
          vendorTeam = { isMasked: true };
        }

        if (MaskedFeeSchedule.isNotMasked(proposalParams.workAgreement.feeSchedule)) {
          feeSchedule = proposalParams.workAgreement.feeSchedule?.map((fs) => FeeScheduleCategorySchema.serialize(fs));
        }

        if (MaskedContracts.isNotMasked(proposalParams.workAgreement.clientPolicies)) {
          clientPolicies = {
            isMasked: false,
            documents: proposalParams.workAgreement.clientPolicies?.map((doc) => ({
              documentInfo: doc.toJSON(),
              documentId: doc.id?.toJSON?.() ?? doc.id.value ?? null,
              replacesId: doc.replacesId?.toJSON?.() ?? doc.replacesId?.value ?? null,
            })) ?? []
          }
        } else {
          clientPolicies = { isMasked: true };
        }

        if (MaskedContracts.isNotMasked(proposalParams.workAgreement.vendorPolicies)) {
          vendorPolicies = {
            isMasked: false,
            documents: proposalParams.workAgreement.vendorPolicies?.map((doc) => ({
              documentInfo: doc.toJSON(),
              documentId: doc.id?.toJSON?.() ?? doc.id.value ?? null,
              replacesId: doc.replacesId?.toJSON?.() ?? doc.replacesId?.value ?? null,
            })) ?? []
          }
        } else {
          vendorPolicies = { isMasked: true };
        }

        if (MaskedContracts.isNotMasked(proposalParams.workAgreement.conflicts)) {
          conflicts = {
            isMasked: false,
            documents: proposalParams.workAgreement.conflicts?.map((doc) => ({
              documentInfo: doc.toJSON(),
              documentId: doc.id?.toJSON?.() ?? doc.id.value ?? null,
              replacesId: doc.replacesId?.toJSON?.() ?? doc.replacesId?.value ?? null,
            })) ?? []
          }
        } else {
          conflicts = { isMasked: true };
        }

        const proposalJSON: ProposalJSON = {
          workAgreementId: proposalParams.workAgreement.id?.toJSON(),
          rfpId: proposalParams.workAgreement.RFPId ? proposalParams.workAgreement.RFPId.toJSON() : undefined,
          id: proposalParams.id?.toJSON(),
          name: proposalParams.workAgreement.name?.toJSON(),
          description: proposalParams.workAgreement.description
            ? proposalParams.workAgreement.description.toJSON()
            : undefined,
          creator: EntityMemberSchema.serialize(proposalParams.creator),
          createdDate: proposalParams.createdDate?.toJSON(),
          lastUpdated: proposalParams.lastUpdated?.toJSON(),
          negotiable: proposalParams.negotiable?.toJSON() ?? true,
          responseDueBy: proposalParams.responseDueBy?.toJSON(),
          clientReviewers: proposalParams.clientReviewers?.map((r) => r.toJSON()) ?? [],
          vendorReviewers: proposalParams.vendorReviewers?.map((r) => r.toJSON()) ?? [],
          supersededById: proposalParams.supersededById?.toJSON(),
          status: enumKeyOf(ProposalStatus, proposalParams.status),
          availableActions: {
            [ProposalAction.Submit]:
              proposalParams.availableActions?.[ProposalAction.Submit]?.map((id) => id.toJSON()) ?? [],
            [ProposalAction.Approve]:
              proposalParams.availableActions?.[ProposalAction.Approve]?.map((id) => id.toJSON()) ?? [],
            [ProposalAction.Reject]:
              proposalParams.availableActions?.[ProposalAction.Reject]?.map((id) => id.toJSON()) ?? [],
            [ProposalAction.Revise]:
              proposalParams.availableActions?.[ProposalAction.Revise]?.map((id) => id.toJSON()) ?? [],
            [ProposalAction.Hire]:
              proposalParams.availableActions?.[ProposalAction.Hire]?.map((id) => id.toJSON()) ?? [],
            [ProposalAction.Delete]:
              proposalParams.availableActions?.[ProposalAction.Delete]?.map((id) => id.toJSON()) ?? [],
            [ProposalAction.Cancel]:
              proposalParams.availableActions?.[ProposalAction.Cancel]?.map((id) => id.toJSON()) ?? [],
            [ProposalAction.Edit]:
              proposalParams.availableActions?.[ProposalAction.Edit]?.map((id) => id.toJSON()) ?? [],
            [ProposalAction.Review]:
              proposalParams.availableActions?.[ProposalAction.Review]?.map((id) => id.toJSON()) ?? [],
          },
          partyRevision: proposalParams.partyRevision
            ? this.serialize({
              ...proposalParams.partyRevision,
              clientReviewers: proposalParams.partyRevision.clientReviewers ?? [],
              vendorReviewers: proposalParams.partyRevision.vendorReviewers ?? [],
              negotiable: proposalParams.partyRevision.negotiable ?? new AHBoolean(true),
              supersedes: undefined,
              partyRevision: undefined,
              userRevision: undefined,
            })
            : undefined,
          userRevision: proposalParams.userRevision
            ? this.serialize({
              ...proposalParams.userRevision,
              clientReviewers: proposalParams.userRevision.clientReviewers ?? [],
              vendorReviewers: proposalParams.userRevision.vendorReviewers ?? [],
              negotiable: proposalParams.userRevision.negotiable ?? new AHBoolean(true),
              supersedes: undefined,
              userRevision: undefined,
              partyRevision: undefined,
            })
            : undefined,
          supersedes: proposalParams.supersedes
            ? this.serialize({
              ...proposalParams.supersedes,
              clientReviewers: proposalParams.supersedes.clientReviewers ?? [],
              vendorReviewers: proposalParams.supersedes.vendorReviewers ?? [],
              negotiable: proposalParams.supersedes.negotiable ?? new AHBoolean(true),
              supersedes: undefined,
              userRevision: undefined,
              partyRevision: undefined,
            })
            : undefined,
          clientTeam: clientTeam,
          additionalVendors: proposalParams.workAgreement.additionalVendors?.map((vendor) => vendor.toJSON()) ?? [],
          vendorTeam: vendorTeam,
          teamRestricted: {
            value: MaskedTeam.isMasked(proposalParams.workAgreement.teamRestricted)
              ? undefined
              : proposalParams.workAgreement.teamRestricted?.value,
            isMasked: MaskedTeam.isMasked(proposalParams.workAgreement.teamRestricted),
          },
          feeSchedule: {
            isMasked: MaskedFeeSchedule.isMasked(proposalParams.workAgreement.feeSchedule),
            categories: MaskedFeeSchedule.isMasked(feeSchedule) ? undefined : feeSchedule,
          },
          startDate: proposalParams.workAgreement.startDate?.toJSON(),
          endDate: proposalParams.workAgreement.endDate?.toJSON(),
          discount: MaskedDiscount.isNotMasked(proposalParams.workAgreement.discount)
            ? { percent: proposalParams.workAgreement.discount?.toJSON() ?? 0, isMasked: false }
            : { percent: null, isMasked: true },
          clientPolicies: clientPolicies,
          vendorPolicies: vendorPolicies,
          conflictsCheckWaived: {
            value: MaskedContracts.isMasked(proposalParams.workAgreement.conflictsCheckWaived)
              ? undefined
              : proposalParams.workAgreement.conflictsCheckWaived?.value,
            isMasked: MaskedContracts.isMasked(proposalParams.workAgreement.conflictsCheckWaived),
          },
          conflicts: conflicts,
          clientTeamTemplateIds: proposalParams.workAgreement.clientTeamTemplateIds?.map((id) => id.toJSON()),
          vendorTeamTemplateIds: proposalParams.workAgreement.vendorTeamTemplateIds?.map((id) => id.toJSON()),
          clientFeeScheduleTemplateIds: proposalParams.workAgreement.clientFeeScheduleTemplateIds?.map((id) =>
            id.toJSON(),
          ),
          vendorFeeScheduleTemplateIds: proposalParams.workAgreement.vendorFeeScheduleTemplateIds?.map((id) =>
            id.toJSON(),
          ),
          hireDate: proposalParams.workAgreement.hireDate?.toJSON(),
          archiveDate: proposalParams.workAgreement.archiveDate?.toJSON(),
          userBuilderJSON: proposalParams.userBuilder
            ? new ProposalBuilderSchema(new WorkAgreementSchema(), this.currentUser).serialize(
              proposalParams.userBuilder,
            )
            : undefined,
          existingRedlineJSON: proposalParams.existingRedline?.toJSON(),
        };
        return proposalJSON;
      },
    });
}
